import logger from "../../../utils/logger";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Form,
  Spinner,
} from "reactstrap";

// redux
import { useDispatch } from "react-redux";
import {
  verifyPasswordCode,
  forgotPassword,
} from "../../../slices/apps/authSlice";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// images
import Logo from "../../../assets/images/logo/metiner_avantaj_logo.png";

const TwoVerify = () => {
  document.title = "SMS Doğrulama | Metiner Avantaj+";

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const phoneNumber = location.state?.phoneNumber;

  const [isLoading, setIsLoading] = useState(true);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [counter, setCounter] = useState(180);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      digit1: "",
      digit2: "",
      digit3: "",
      digit4: "",
      digit5: "",
      digit6: "",
    },

    validationSchema: Yup.object(
      [...Array(6)].reduce((schema, _, i) => {
        schema[`digit${i + 1}`] = Yup.string()
          .matches(/^\d$/, "Sadece rakam giriniz")
          .required("Gerekli");
        return schema;
      }, {})
    ),

    onSubmit: async (values) => {
      setVerifyLoading(true);
      try {
        const code = Object.values(values).join("");
        await dispatch(verifyPasswordCode({ phoneNumber, code })).unwrap();

        navigate("/reset-password", {
          state: { phoneNumber: phoneNumber },
        });
      } catch (error) {
        logger.error("Doğrulama başarısız:", error);
      } finally {
        setVerifyLoading(false);
      }
    },
  });

  useEffect(() => {
    if (counter <= 0) return;
    const t = setTimeout(() => setCounter(counter - 1), 1000);
    return () => clearTimeout(t);
  }, [counter]);

  useEffect(() => {
    if (!phoneNumber) {
      navigate("/forgot-password");
    } else {
      setIsLoading(false);
    }
  }, [phoneNumber, navigate]);

  useEffect(() => {
    if ("OTPCredential" in window) {
      const ac = new AbortController();
      navigator.credentials
        .get({
          otp: { transport: ["sms"] },
          signal: ac.signal,
        })
        .then((otp) => {
          if (otp?.code) {
            const code = otp.code;
            [...code].slice(0, 6).forEach((v, i) => {
              validation.setFieldValue(`digit${i + 1}`, v);
            });
            inputRefs.current[5]?.focus();
          }
        })
        .catch(() => {});

      return () => ac.abort();
    }
  }, []);

  const handleInput = (index, e) => {
    const val = e.target.value;

    if (!/^\d?$/.test(val)) {
      e.target.value = "";
      validation.setFieldValue(`digit${index + 1}`, "");
      return;
    }

    validation.setFieldValue(`digit${index + 1}`, val);

    if (val && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!validation.values[`digit${index + 1}`] && index > 0) {
        inputRefs.current[index - 1].focus();
      }
      validation.setFieldValue(`digit${index + 1}`, "");
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    const code = pasted.slice(0, 6);

    [...code].forEach((v, i) => {
      inputRefs.current[i].value = v;
      validation.setFieldValue(`digit${i + 1}`, v);
    });

    const lastIndex = Math.min(code.length - 1, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      await dispatch(forgotPassword(phoneNumber)).unwrap();
      setCounter(180);
    } catch (e) {
      logger.error("Kod gönderilemedi:", e);
    } finally {
      setResendLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div id="preloader">
        <div id="status">
          <img
            src={Logo}
            alt="Metiner Avantaj+"
            className="mb-2"
            style={{ objectFit: "contain", width: "10rem" }}
          />
          <div className="spinner-border text-primary avatar-xs" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-wrapper d-flex justify-content-center align-items-center min-vh-100">
      <div className="auth-page-content">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="text-center pt-5">
                  <Link to="/" className="d-inline-block auth-logo">
                    <img src={Logo} alt="Metiner Avantaj+" height="100" />
                  </Link>
                </div>

                <CardBody className="p-lg-5 p-4">
                  <div className="text-center mb-4">
                    <h4 className="mb-1">Telefonunuzu Doğrulayın</h4>
                    <p className="text-muted">
                      <span className="fw-semibold">0{phoneNumber}</span>{" "}
                      numarasına gelen 6 haneli kodu girin.
                    </p>
                  </div>

                  <Form onSubmit={validation.handleSubmit}>
                    <Row className="g-2 justify-content-center">
                      {[1, 2, 3, 4, 5, 6].map((digit, index) => (
                        <Col key={digit} xs={2}>
                          <input
                            type="text"
                            maxLength="1"
                            inputMode="numeric"
                            className="form-control form-control-lg bg-light border-light text-center fs-4 p-0"
                            ref={(el) => (inputRefs.current[index] = el)}
                            value={validation.values[`digit${digit}`]}
                            onInput={(e) => handleInput(index, e)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={index === 0 ? handlePaste : undefined}
                            autoComplete={index === 0 ? "one-time-code" : "off"}
                          />
                        </Col>
                      ))}
                    </Row>

                    {validation.errors.digit1 && (
                      <div className="invalid-feedback d-block text-center mt-2">
                        Lütfen tüm alanları doldurun
                      </div>
                    )}

                    <Button
                      type="submit"
                      color="primary"
                      className="w-100 mt-4"
                      disabled={verifyLoading}
                    >
                      {verifyLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Doğrulanıyor...
                        </>
                      ) : (
                        "Doğrula"
                      )}
                    </Button>
                  </Form>

                  <div className="mt-4 text-center">
                    {counter > 0 ? (
                      <p className="text-muted">
                        Tekrar kod için{" "}
                        <span className="fw-semibold text-primary">
                          {counter} saniye
                        </span>{" "}
                        bekleyin.
                      </p>
                    ) : (
                      <Button
                        color="link"
                        className="p-0 fw-semibold text-primary text-decoration-underline"
                        onClick={handleResendCode}
                        disabled={resendLoading}
                      >
                        {resendLoading ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Gönderiliyor...
                          </>
                        ) : (
                          "Kodu Tekrar Gönder"
                        )}
                      </Button>
                    )}
                  </div>

                  <div className="mt-2 text-center">
                    <Link
                      to="/forgot-password"
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      Geri Dön
                    </Link>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <footer className="footer h-auto pb-2">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center text-muted">
                <p className="mb-2" style={{ letterSpacing: "0.8px" }}>
                  Metiner Avantaj+ © {new Date().getFullYear()} | 0 352 3 555
                  444 | metiner@boschservice.com.tr
                  <br />
                  <i className="mdi mdi-heart text-danger"></i> by{" "}
                  <a
                    href="https://kodexis.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "#007bff" }}
                  >
                    Kodexis
                  </a>
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default TwoVerify;
